import * as React from 'react';
import { styled, ThemeProvider, useTheme } from '@mui/material/styles';
import MuiAccordion, {
  type AccordionProps as MAccordionProps,
} from '@mui/material/Accordion';
import MuiAccordionSummary, {
  type AccordionSummaryProps,
} from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { theme } from '../../../Theme';

type Size = 'sm' | 'lg';

export type AccordionProps = Omit<MAccordionProps, 'children'> & {
  expandIcon?: React.ReactNode;

  size?: Size;
  title?: string;
  titleBold?: string;
  titlecolor?: string;
  InfoDetails?: React.ReactNode;
  AssessDetails?: React.ReactNode;
  disableExpand?: boolean; // to disable the collapse function
};
type AccordionDetailsProps = {
  details: React.ReactNode;
  info?: boolean;
};
const AccordionDetails = styled((props: AccordionDetailsProps) => {
  const { details, info } = props;
  const theme = useTheme();
  return (
    <ThemeProvider theme={theme}>
      <MuiAccordionDetails
        sx={{
          paddingX: '1.5rem',
          ...(info && { background: theme.palette.grey['200'] }),
        }}
      >
        {details}
      </MuiAccordionDetails>
    </ThemeProvider>
  );
})();

type CustomAccordionSummaryProps = AccordionSummaryProps & {
  titlecolor?: string;
  size?: string;
  theme?: any;
};

export const CustomAccordionSummary = styled(
  (props: CustomAccordionSummaryProps) => (
    <MuiAccordionSummary
      expandIcon={
        <ExpandMoreIcon
          sx={{
            color: props.titlecolor ?? theme.palette.grey['600'],
            transform: 'rotate(-90deg)',
          }}
        />
      }
      {...props}
    />
  ),
)<CustomAccordionSummaryProps>(({ theme, size }) => ({
  ...(size === 'sm' ? {} : { flexDirection: 'row-reverse' }),

  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },

  '& .MuiAccordionSummary-content': {
    ...(size === 'sm'
      ? {
          // When size is 'sm', allow the title to grow as needed without wrapping
          flex: 'none',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          // Adjust the margin to bring the icon closer to the title
          marginLeft: theme.spacing(-2),
        }
      : { maxWidth: '100%', marginLeft: theme.spacing(1.5) }),
  },
  justifyContent: 'start',
}));

export const AssessmentAccordion: React.FunctionComponent<AccordionProps> = (
  props: AccordionProps,
) => {
  const theme = useTheme();
  const {
    title,
    titleBold = '',
    InfoDetails,
    AssessDetails,
    titlecolor,
    defaultExpanded = true,
    disableExpand = false,
    size,
    ...others
  } = props;

  const accordionInfoDetails = <AccordionDetails details={InfoDetails} info />;

  const accordionAssessDetails = <AccordionDetails details={AssessDetails} />;

  return (
    <MuiAccordion
      {...others}
      defaultExpanded={defaultExpanded || disableExpand}
      expanded={disableExpand ? true : undefined}
      sx={{ ':before': { display: 'none' }, boxShadow: 'none' }}
    >
      <CustomAccordionSummary
        aria-controls="accordion-content"
        id="accordion-header"
        titlecolor={titlecolor ?? theme.palette.grey['600']}
        size="1.2rem"
        onClick={event => {
          disableExpand && event.stopPropagation();
        }}
      >
        <Typography
          sx={{
            color: titlecolor ?? theme.palette.grey['900'],

            ...(size === 'sm'
              ? { fontFamily: 'inherit', fontSize: 14 }
              : {
                  fontWeight: 'bold',
                  fontSize: 'normal',
                }),
          }}
        >
          {title}
          <b>{titleBold}</b>
        </Typography>
      </CustomAccordionSummary>
      {InfoDetails !== undefined && accordionInfoDetails}
      {accordionAssessDetails}
    </MuiAccordion>
  );
};
